import React, { useState } from 'react';
import Panel from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Inventory from '../../components/ui/Inventory';
import { InventoryItem } from '../../lib/InventorySlot';

const starter: Array<InventoryItem | null> = [
  { name: 'Coins', quantity: 125000 },
  { name: 'Shark', quantity: 12 },
  { name: 'Rune scimitar', quantity: 1 },
  null,
  { name: 'Bones', quantity: 100000 },
  null,
  { name: 'Law rune', quantity: 2500000 },
  null,
];

const InventoryPage: React.FC = () => {
  const [items, setItems] = useState(starter);
  const [selected, setSelected] = useState<number | null>(0);

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Inventory Management</h1>
      <p className="text-osrs-yellow mb-8">
        The inventory is 4 columns by 7 rows, 28 slots. A selected slot has a white outline. Stack counts stay as a full number until 100,000, then switch to K, then to M at 10,000,000.
      </p>

      <section id="grid-layout" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Grid Layout</h2>
        <Panel>
          <Inventory
            items={items}
            columns={4}
            rows={7}
            selectedIndex={selected}
            onSelect={(index) => setSelected(index)}
          />
        </Panel>
      </section>

      <section id="drag-and-drop" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Drag and Drop</h2>
        <Panel>
          <p className="text-osrs-yellow mb-4">
            Pick a slot, then pick an empty slot to move the item. This is the same gesture as dragging inside the bank, expressed as two clicks so it works without a pointer.
          </p>
          <Button
            onClick={() => {
              if (selected === null) return;
              const empty = items.findIndex((item) => item === null);
              if (empty < 0 || empty === selected) return;
              setItems((current) => {
                const next = current.slice();
                while (next.length < 28) next.push(null);
                const moving = next[selected];
                next[selected] = null;
                next[empty] = moving;
                return next;
              });
              setSelected(empty);
            }}
          >
            Move selected to first empty slot
          </Button>
        </Panel>
      </section>

      <section id="stacking" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Stacking Items</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>1,500 coins render as 1500.</li>
            <li>100,000 bones render as 100K.</li>
            <li>2,500,000 law runes render as 2500K.</li>
            <li>10,000,000 of an item renders as 10M.</li>
          </ul>
        </Panel>
      </section>

      <section id="organization" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Organization</h2>
        <Panel>
          <p className="text-osrs-yellow">
            Empty slots stay in the grid. Do not collapse them. A player reads position, and a missing cell would shift every item after it.
          </p>
        </Panel>
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use 4 by 7 for an inventory and a wider column count for a bank.</li>
            <li>Pass your own item image. This package does not ship Jagex item sprites.</li>
            <li>Give every occupied slot a name. The name is the accessible label.</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default InventoryPage;
